import React, { useState } from 'react'
import { useFormik } from 'formik'
import intl from 'react-intl-universal'
import { connect, DispatchProp } from 'react-redux'
import {
  Modal,
  ModalBody,
  ModalFooter,
  FormFeedback,
  Label,
  Input,
  Button,
} from 'reactstrap'
import * as Yup from 'yup'
import { validateAnnotationSquema } from '~/reactapp/shared/annotations/annotation.service'
import { updateCreateAnnotation } from '~/redux/settingsReducer'
import { AnnotationType, AnnotationUserData } from '~/types/annotation'
import { isBoolean } from 'lodash'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./AnnotationTypeModal.styles.scss')

interface Props {
  visible: boolean
  subtype?: string
  toggle: () => void
  data: AnnotationType | undefined
  store: any
}
interface CategoryErrors {
  nameError: string | undefined
  categoryIdError: string | undefined
  unknownError: string | undefined
}

interface IRequestState {
  loading: boolean
  error: string | undefined
}

function AnnotationModal(props: Props & DispatchProp) {
  const mode = props.data ? 'update' : 'new'
  const [requestState, setRequestState] = useState<IRequestState>({
    loading: false,
    error: undefined,
  })

  const validateCategory = (
    schema: AnnotationUserData,
    name: string
  ): CategoryErrors => {
    const errors: CategoryErrors = {
      nameError: undefined,
      categoryIdError: undefined,
      unknownError: undefined,
    }

    // Validate posible redux missing properties
    const { settings } = props.store.getState()
    if (!settings.annotations) {
      errors.unknownError = intl.get(
        'settings.annotations.modal.error.unknownError'
      )
      return errors
    }

    // Validate existing category
    const annotationType = settings.annotations.find((annotation) => {
      if (
        (annotation.name === name ||
          annotation.categoryId === schema.categoryId) &&
        annotation.id !== props.data?.id // Discart the annotation when updating it
      ) {
        return true
      }
      return false
    })
    if (!annotationType) return errors
    if (annotationType.name === name) {
      errors.nameError = intl.get('settings.annotations.modal.error.nameExists')
    } else if (annotationType.categoryId === schema.categoryId) {
      errors.categoryIdError = intl.get(
        'settings.annotations.modal.error.categoryId'
      )
    } else {
      errors.unknownError = intl.get(
        'settings.annotations.modal.error.unknownError'
      )
    }
    return errors
  }

  const onSubmitAnnotationType = async ({ name, link }) => {
    setRequestState({ loading: true, error: undefined })
    const validSchema = await validateAnnotationSquema(link)
    if (!isBoolean(validSchema)) {
      const { nameError, categoryIdError, unknownError } = validateCategory(
        validSchema,
        name
      )
      if (nameError !== undefined) {
        setRequestState({ loading: false, error: nameError })
      } else if (categoryIdError !== undefined) {
        setRequestState({ loading: false, error: categoryIdError })
      } else if (unknownError !== undefined) {
        setRequestState({ loading: false, error: unknownError })
      } else {
        props.dispatch(
          updateCreateAnnotation({
            name,
            link,
            categoryId: validSchema.categoryId,
            id: props.data?.id ?? Date.now(),
          } as AnnotationType)
        )
        props.toggle()
      }
    } else {
      setRequestState({
        loading: false,
        error: intl.get('settings.annotations.modal.jsonSchemaInvalid'),
      })
    }
  }

  const annotationSchema = Yup.object().shape({
    name: Yup.string().required(
      intl.get('settings.annotations.modal.requiredField')
    ),
    link: Yup.string()
      .url(intl.get('settings.annotations.modal.invalidURL'))
      .matches(
        /^https:\/\//gi,
        intl.get('settings.annotations.modal.error.https')
      )
      .required(intl.get('settings.annotations.modal.requiredField')),
  })

  const formik = useFormik({
    initialValues: {
      name: props.data?.name ?? '',
      link: props.data?.link ?? '',
    },
    validationSchema: annotationSchema,
    onSubmit: onSubmitAnnotationType,
  })

  const onFieldChange = (e) => {
    setRequestState({ loading: false, error: undefined })
    formik.handleChange(e)
  }

  return (
    <Modal
      wrapClassName="bootstrap4"
      size="lg"
      className={styles.root}
      centered
      isOpen
      toggle={props.toggle}
      data-testid="settings-create/update-annotations-modal"
    >
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <h4 className="title">
            {intl.get(`settings.annotations.modal.title.${mode}`)}
          </h4>
          <p>{intl.get(`settings.annotations.modal.subtitle.${mode}`)}</p>
          <div className="group">
            <Label for="name">
              {intl.get('settings.annotations.modal.nameField')}
            </Label>
            <Input
              id="name"
              name="name"
              placeholder={intl.get(
                'settings.annotations.modal.name.placeholder'
              )}
              type="text"
              onChange={onFieldChange}
              value={formik.values.name}
              invalid={formik.errors.name !== undefined && formik.touched.name}
            />
            <FormFeedback data-testid="settings-annotations-name-error">
              {formik.errors.name}
            </FormFeedback>
          </div>
          <div className="group">
            <Label for="link">
              {intl.get('settings.annotations.modal.linkField')}
            </Label>
            <Input
              id="link"
              name="link"
              placeholder={intl.get(
                'settings.annotations.modal.link.placeholder'
              )}
              onChange={onFieldChange}
              value={formik.values.link}
              invalid={formik.errors.link !== undefined && formik.touched.link}
            />
            <FormFeedback data-testid="settings-annotations-link-error">
              {formik.errors.link}
            </FormFeedback>
          </div>
          {requestState.error && (
            <div
              data-testid="settings-annotations-schema-error"
              className="schema-error"
            >
              {requestState.error}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="footer">
          <Button
            outline
            color="secondary"
            onClick={props.toggle}
            data-testid="settings-annotations-cancel-btn"
          >
            {intl.get('settings.annotations.modal.cancel.btn')}
          </Button>
          <Button
            type="submit"
            color="primary"
            className="d-flex align-items-center justify-content-center"
            disabled={requestState.loading}
            data-testid="settings-annotations-create/update-btn"
          >
            {intl.get(`settings.annotations.modal.${mode}.btn`)}
            {requestState.loading && (
              <div
                className="spinner-border spinner-border-sm text-light ml-2"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default connect()(AnnotationModal)
